import type {
  DataSources, AgentSource, OrdersSource, ClientsSource, TargetsSource,
  GamificationRulesSource, ProductsSource, SeasonalDataSource,
  SeasonalActualRow, SeasonalTargetRow,
  Agent, Client, Order, Product, AgentTargets, Period, Season, GamificationRules,
} from '@losan/core';
import { normalizeLine } from '@losan/core';

/**
 * Browser-side counterpart to packages/core's SnapshotSources.
 *
 * The Node-side SnapshotSources read JSON from disk via readFileSync, which
 * doesn't exist in the browser. This file fetches the same JSON snapshots
 * (copied into apps/web/public/snapshots/) over HTTP at runtime.
 *
 * Why duplicate code? Because the security boundary is real: in production we
 * never want Power BI credentials in the browser bundle. The snapshots ARE
 * the contract between the (Node) extractor and the (browser) consumer.
 */

const BASE = '/snapshots';

const cache = new Map<string, Promise<unknown>>();

function fetchJson<T>(file: string): Promise<T> {
  const cached = cache.get(file);
  if (cached) return cached as Promise<T>;
  const p = fetch(`${BASE}/${file}`).then((res) => {
    if (!res.ok) throw new Error(`Failed to load snapshot ${file}: ${res.status}`);
    return res.json() as Promise<T>;
  });
  cache.set(file, p);
  return p;
}

interface RawAgent  { agente_id: number; agente: string; pais_id: string | null; Act: boolean; }
interface RawClient { Id: string; Des: string; Pais_Id: string | null; Nif: string | null; Lat: string | null; Lon: string | null; }
interface RawOrder  { Pedido_Id: string; Fecha: string; Agente_ID: string; Cliente_Id: string; Coleccion: string; Total: number | null; Unidades: number | null; }
interface RawTarget { Cliente_Id: string; EUR_target: number; Unidades_target: number; }
interface RawProduct { Sku: string; Tipo: string; Coleccion: string; Familia: string | null; SubTipo: string | null; }
interface RawSeasonalActual { Agente_Id: string; Cliente_Id: string; Tipo: string; Coleccion: string; Temporada: string; SemanaAnyoNum: number; EUR: number; Unidades: number; }
interface RawSeasonalTarget { Agente_Id: string; Cliente_Id: string; Tipo: string; Coleccion: string; Temporada: string; SemanaAnyoNum: number; EUR_target: number; Unidades_target: number; Source_Temporada: string; }

const parseCoord = (v: string | null | undefined): number => {
  if (v == null) return NaN;
  const n = parseFloat(String(v).trim());
  return Number.isFinite(n) ? n : NaN;
};

class BrowserAgentSource implements AgentSource {
  readonly provenance = 'real' as const;
  async getAgent(agentId: string): Promise<Agent> {
    const raw = await fetchJson<RawAgent[]>('agents.json');
    const match = raw.find((a) => String(a.agente_id) === String(agentId));
    if (!match) return { id: agentId, email: '', name: 'Agente desconocido', role: 'sales' };
    return { id: String(match.agente_id), email: '', name: match.agente, role: match.Act ? 'sales' : 'inactive' };
  }
  async listAgents(): Promise<Agent[]> {
    const raw = await fetchJson<RawAgent[]>('agents.json');
    return raw.map((a) => ({
      id: String(a.agente_id), email: '', name: a.agente, role: a.Act ? 'sales' : 'inactive',
    }));
  }
}

class BrowserOrdersSource implements OrdersSource {
  readonly provenance = 'real' as const;
  async getOrders(agentId: string): Promise<Order[]> {
    const raw = await fetchJson<RawOrder[]>('orders-last12m.json');
    return raw
      .filter((o) => String(o.Agente_ID) === String(agentId))
      .map((o) => ({
        id: o.Pedido_Id,
        agentId: String(o.Agente_ID),
        clientId: String(o.Cliente_Id),
        date: o.Fecha,
        line: normalizeLine(o.Coleccion),
        totalEUR: Number(o.Total ?? 0),
        lines: [],
      }));
  }
}

class BrowserClientsSource implements ClientsSource {
  readonly provenance = 'real' as const;
  async getClients(): Promise<Client[]> {
    const raw = await fetchJson<RawClient[]>('clients.json');
    return raw
      .map((c): Client | null => {
        const lat = parseCoord(c.Lat);
        const lng = parseCoord(c.Lon);
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
        return {
          id: c.Id,
          commercialName: c.Des,
          location: { lat, lng },
          revenueYTD: 0,
          lastOrderDate: null,
        };
      })
      .filter((c): c is Client => c !== null);
  }
}

class BrowserTargetsSource implements TargetsSource {
  readonly provenance = 'real' as const;
  async getTargets(agentId: string, period: Period): Promise<AgentTargets> {
    const raw = await fetchJson<RawTarget[]>('targets-ss26.json');
    const totalEUR = raw.reduce((s, r) => s + r.EUR_target, 0);
    const totalUnits = raw.reduce((s, r) => s + r.Unidades_target, 0);
    const totalClients = new Set(raw.map((r) => r.Cliente_Id)).size;
    const factor = period === 'month' ? 1 / 6 : period === 'quarter' ? 0.5 : 1;
    return {
      agentId,
      period,
      revenueEUR: Math.round(totalEUR * factor),
      units: Math.round(totalUnits * factor),
      clientCount: Math.round(totalClients * factor),
    };
  }
}

class BrowserProductsSource implements ProductsSource {
  readonly provenance = 'real' as const;
  async getProducts(): Promise<Product[]> {
    const raw = await fetchJson<RawProduct[]>('products.json');
    return raw.map((p): Product => ({
      sku: p.Sku, category: p.Tipo, line: p.Coleccion,
      ...(p.Familia ? { family: p.Familia } : {}),
      ...(p.SubTipo ? { subtype: p.SubTipo } : {}),
    }));
  }
}

// Inline mock rules — kept in sync with packages/core/src/infrastructure/mock/fixtures.ts.
// Centralising here would require a Node-safe import from @losan/core; the
// dashboard's design intentionally keeps this duplicate small (one constant)
// to avoid coupling the browser shell to Node-only file reads.
const RULES: GamificationRules = {
  badges: [
    { id: 'super_vendedor',   label: 'Súper Vendedor',     description: 'Superar el 110% del objetivo de facturación mensual.' },
    { id: 'cazador_clientes', label: 'Cazador de Clientes', description: 'Incrementar un 15% el número de clientes vs año anterior.' },
    { id: 'ruta_perfecta',    label: 'Ruta Perfecta',       description: 'Reactivar 3 clientes que estaban en estado Rojo.' },
    { id: 'multi_categoria',  label: 'Multi-Categoría',     description: 'Vender en al menos 5 categorías distintas en el mes.' },
    { id: 'cross_line',       label: 'Cross-Line',          description: 'Vender en las 4 líneas en el mes (MAN, WOMAN, TEEN, KIDS).' },
    { id: 'ticket_alto',      label: 'Ticket Alto',         description: 'Ticket medio por pedido un 10%+ por encima de la media de tu país.' },
    { id: 'sello_premium',    label: 'Sello Premium',       description: 'Mantener un margen comercial igual o superior al 35% en el mes.' },
    { id: 'comeback_cliente', label: 'Comeback',            description: 'Reactivar un cliente que llevaba 6+ meses sin pedidos.' },
  ],
  bonus: {
    baseCommissionPct: 0.10,
    maxCommissionPct: 0.12,
    tieredAtPctOfTarget: 1.10,
    minBonusEUR: 0,
  },
  reactivationThresholdForRutaPerfecta: 3,
  minCategoriesForMultiCategoria: 5,
  minLinesForCrossLine: 4,
  ticketAltoOverAveragePct: 0.10,
  selloPremiumMarginPct: 0.35,
  comebackDormantMonths: 6,
};

class BrowserGamificationRulesSource implements GamificationRulesSource {
  readonly provenance = 'mock' as const;
  async getRules(): Promise<GamificationRules> { return RULES; }
}

class BrowserSeasonalDataSource implements SeasonalDataSource {
  readonly provenance = 'real' as const;
  async listSeasons(): Promise<Season[]> {
    return fetchJson<Season[]>('seasons.json');
  }
  async getActuals(): Promise<SeasonalActualRow[]> {
    const raw = await fetchJson<RawSeasonalActual[]>('orders-by-season.json');
    return raw.map((r) => ({
      agenteId: r.Agente_Id,
      clienteId: r.Cliente_Id,
      category: r.Tipo,
      line: normalizeLine(r.Coleccion),
      season: r.Temporada,
      weekNumber: r.SemanaAnyoNum,
      revenueEUR: r.EUR,
      units: r.Unidades,
    }));
  }
  async getTargets(): Promise<SeasonalTargetRow[]> {
    const raw = await fetchJson<RawSeasonalTarget[]>('targets-by-season.json');
    return raw.map((r) => ({
      agenteId: r.Agente_Id,
      clienteId: r.Cliente_Id,
      category: r.Tipo,
      line: normalizeLine(r.Coleccion),
      season: r.Temporada,
      weekNumber: r.SemanaAnyoNum,
      revenueTargetEUR: r.EUR_target,
      unitsTarget: r.Unidades_target,
      sourceSeason: r.Source_Temporada,
    }));
  }
}

export function createBrowserSnapshotSources(): DataSources {
  return {
    agent: new BrowserAgentSource(),
    orders: new BrowserOrdersSource(),
    clients: new BrowserClientsSource(),
    targets: new BrowserTargetsSource(),
    gamification: new BrowserGamificationRulesSource(),
    products: new BrowserProductsSource(),
    seasonal: new BrowserSeasonalDataSource(),
  };
}
